import React, {
  FC,
  PropsWithChildren,
  Suspense,
  createContext,
  lazy,
  memo,
  useCallback,
  useContext,
  useState,
} from 'react';
import { isUndefined } from 'lodash';

import TimelinePlayBarPlaceholder from './TimelinePlayBar/components/TimelinePlayBarPlaceholder';
import BeatsourcePlayBarPlaceholder from './BeatsourcePlayBar/components/BeatsourcePlayBarPlaceholder';

import { TNavigateDirection, TSongPlayBarContext, TSongData } from './interfaces';

const TimelinePlayBar = lazy(() => import('./TimelinePlayBar'));
const BeatsourcePlayBar = lazy(() => import('./BeatsourcePlayBar'));

export const SongPlayBarContext = createContext<TSongPlayBarContext>(undefined!);

export const useSongPlayBar = () => useContext(SongPlayBarContext);

export const SongPlayBarContextProvider: FC<PropsWithChildren> = memo(({ children }) => {
  const [beatsourceTrack, seteatsourceTrack] = useState<Nullable<BeatsourceTrack>>(null);
  const [songData, setSongData] = useState<Nullable<TSongData>>(null);
  const [isSongPaused, setIsSongPaused] = useState<boolean>(false);

  const playBeatsoureSong = useCallback((track: Nullable<BeatsourceTrack>) => {
    seteatsourceTrack(track);
  }, []);

  const playSong = useCallback((data: Nullable<TSongData>) => {
    setSongData(data);
  }, []);

  const close = useCallback(() => {
    playBeatsoureSong(null);
    playSong(null);
  }, []);

  const navigate = (direction: TNavigateDirection) => {
    const isPrev = direction === '<';
    const currentIdx = songData?.tracks?.findIndex(({ _id }) => _id === songData?.track?._id);

    const newTrack = !isUndefined(currentIdx) && songData?.tracks?.[currentIdx + (isPrev ? -1 : 1)];

    newTrack &&
      setSongData(prev => ({
        ...prev,
        track: newTrack,
      }));
  };

  return (
    <SongPlayBarContext.Provider
      value={{
        beatsourceTrack,
        isSongPaused,
        setIsSongPaused,
        playBeatsoureSong,
        playSong,
        songData,
        close,
        navigate,
      }}
    >
      {!!beatsourceTrack ? (
        <Suspense fallback={<BeatsourcePlayBarPlaceholder />}>
          <BeatsourcePlayBar track={beatsourceTrack} key={beatsourceTrack.id} />
        </Suspense>
      ) : null}
      {!!songData?.track ? (
        <Suspense fallback={<TimelinePlayBarPlaceholder />}>
          <TimelinePlayBar track={songData.track} key={songData.track?._id} />
        </Suspense>
      ) : null}
      {children}
    </SongPlayBarContext.Provider>
  );
});
